import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { POSTHOG_EVENTS } from "../../../../../constants/PostHogEvents";
import { USER_SETTINGS } from "../../../../../constants/UserConstants.ts";
import { SESSION_STORAGE_KEYS } from "../../../../../helpers/sessionStorage.ts";
import { usePostHog } from "../../../../../hooks/usePostHog";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import { getFiboLabVariant } from "../../../../../utils";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";

const EXIT_TOP_THRESHOLD_PX = 12;
const EXIT_THROTTLE_MS = 16;
const EXIT_INTENT_DEBOUNCE_MS = 100;

export const useFiboUsage = () => {
	const { authStore, fiboUsageStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const labVariant = getFiboLabVariant();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [disableExitIntent, setDisableExitIntent] = useState(false);
	const openingRef = useRef(false);
	const lastExitIntentTime = useRef(0);

	const sessionStorage = useMemo(() => (typeof window !== "undefined" ? window.sessionStorage : undefined), []);

	const wasUsageShown = useCallback(
		() => sessionStorage?.getItem(SESSION_STORAGE_KEYS.usageShown) === "true",
		[sessionStorage],
	);
	const wasExitShown = useCallback(
		() => sessionStorage?.getItem(SESSION_STORAGE_KEYS.exitShown) === "true",
		[sessionStorage],
	);
	const setUsageShown = useCallback(
		() => sessionStorage?.setItem(SESSION_STORAGE_KEYS.usageShown, "true"),
		[sessionStorage],
	);
	const setExitShown = useCallback(
		() => sessionStorage?.setItem(SESSION_STORAGE_KEYS.exitShown, "true"),
		[sessionStorage],
	);

	const openPopupOnce = useCallback(() => {
		if (openingRef.current || isPopupOpen) return;
		openingRef.current = true;
		setIsPopupOpen(true);
		// Track popup opened
		if (labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_USAGE_POPUP_OPENED, {
				lab_variant: labVariant,
			});
		}
		// Use requestAnimationFrame for better performance
		requestAnimationFrame(() => {
			openingRef.current = false;
		});
	}, [isPopupOpen, labVariant, trackEvent]);

	const handleClosePopup = useCallback(() => {
		setIsPopupOpen(false);
		// Track popup closed
		if (labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_USAGE_POPUP_CLOSED, {
				lab_variant: labVariant,
			});
		}
	}, [labVariant, trackEvent]);

	// Load user setting to optionally disable exit-intent when discounted starter popup was shown
	useEffect(() => {
		let isMounted = true;
		const load = async () => {
			try {
				const value = await authStore.user?.getFreshSettingsCopy?.(
					USER_SETTINGS.fiboLabsStarterDiscountedShown,
				);
				if (isMounted) setDisableExitIntent(Boolean(value));
			} catch {
				// no-op
			}
		};
		if (authStore.user) {
			load();
		} else {
			setDisableExitIntent(false);
		}
		return () => {
			isMounted = false;
		};
	}, [authStore.user]);

	// Exit intent detection
	useEffect(() => {
		if (!fiboUsageStore.isEligibleForUsagePopup()) return;
		if (typeof window === "undefined") return;
		if (disableExitIntent) return;
		if (isPopupOpen || wasExitShown() || !fiboUsageStore.canShowExitIntentPopupThisSession()) return;

		let lastInvoke = 0;

		const onMouseMove = (e: MouseEvent) => {
			const now = Date.now();
			if (now - lastInvoke < EXIT_THROTTLE_MS) return;
			lastInvoke = now;

			// Early exit if conditions are not met
			if (e.clientY > EXIT_TOP_THRESHOLD_PX || e.movementY >= 0) return;

			// Debounce to prevent multiple rapid triggers
			if (now - lastExitIntentTime.current < EXIT_INTENT_DEBOUNCE_MS) return;
			lastExitIntentTime.current = now;

			// Show popup immediately when user moves to top
			openPopupOnce();
			fiboUsageStore.markExitIntentPopupShown();
			setExitShown();
		};

		window.addEventListener("mousemove", onMouseMove, { passive: true });
		return () => {
			window.removeEventListener("mousemove", onMouseMove);
		};
	}, [authStore, isPopupOpen, wasExitShown, openPopupOnce, setExitShown, fiboUsageStore, disableExitIntent]);

	const makeHandleActionClick = useCallback(() => {
		fiboUsageStore.registerGenerationRefineClick();

		if (!fiboUsageStore.isEligibleForUsagePopup()) return;

		if (!wasUsageShown() && fiboUsageStore.canShowUsagePopupThisSession()) {
			openPopupOnce();
			fiboUsageStore.markUsagePopupShown();
			setUsageShown();
		}
	}, [fiboUsageStore, wasUsageShown, openPopupOnce, setUsageShown]);

	// Keep popup mounted while it's open, even if eligibility changes (e.g., discount loaded)
	const shouldRenderPopup = (isPopupOpen || fiboUsageStore.isEligibleForUsagePopup()) && !iframeStore.isIframe();

	return {
		isPopupOpen,
		handleClosePopup,
		makeHandleActionClick,
		shouldRenderPopup,
	};
};
